import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'expo-router'
import {
  submitVoiceAssessmentMeasurement,
  VOICE_ASSESSMENT_UPLOAD_ERROR,
} from '@/src/features/voice-assessment/services/upload-voice-assessment'
import {
  getVoiceAssessmentCompletedMeasurements,
  getVoiceAssessmentMeasurementAnalytics,
  getVoiceAssessmentReflectionAnalytics,
  getVoiceAssessmentVoiceType,
} from '@/src/features/voice-assessment/utils/voice-assessment-analytics'
import { buildVoiceAssessmentRoute } from '@/src/features/voice-assessment/utils/voice-assessment-route'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useRequiredAppSessionUser } from '@/src/hooks/useRequiredAppSessionUser'
import { useToast } from '@/src/hooks/useToast'
import { hasSavedMeasurementData } from '@/src/queries/measurement-queries'
import {
  backendSessionQueryOptions,
  sessionDefinitionQueryOptions,
  useAbortSessionMutation,
  useEndSessionMutation,
} from '@/src/queries/session-queries'
import {
  buildVoiceAssessmentAbandonedProperties,
  buildVoiceAssessmentCompletedProperties,
  buildVoiceAssessmentMeasurementQuestionsCompletedProperties,
  buildVoiceAssessmentMeasurementRecordingCompletedProperties,
  buildVoiceAssessmentMeasurementSubmittedProperties,
  buildVoiceAssessmentResumedProperties,
  MixpanelEvents,
  trackEvent,
} from '@/src/services/analytics'
import { showAlert } from '@/src/utils/alert'
import { captureException } from '@/src/utils/sentry'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { VoiceAssessmentFlowController } from './VoiceAssessmentFlow.shared'
import {
  buildInitialVoiceAssessmentState,
  makeVoiceAssessmentReducer,
} from './VoiceAssessmentFlow.reducer'
import { resolveBackendResumeTarget } from './VoiceAssessmentFlow.shared'

const VOICE_ASSESSMENT_FINISH_ROUTE =
  '/(protected)/(account)/voice-assessment-finish' as const

export function useVoiceAssessmentFlowController({
  backendSessionId,
  isOnboardingEntry = false,
}: {
  backendSessionId: number
  isOnboardingEntry?: boolean
}) {
  const { back, canGoBack, push, replace } = useRouter()
  const { t } = useTranslation(['common', 'onboarding'])
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const abortSessionMutation = useAbortSessionMutation()
  const completeAssessmentMutation = useEndSessionMutation()
  const contentfulLanguage = useContentfulLocale()
  const { backendUser } = useRequiredAppSessionUser()
  const voiceType = getVoiceAssessmentVoiceType(backendUser)

  const backendSessionQuery = useSuspenseQuery(
    backendSessionQueryOptions(backendSessionId),
  )
  const sessionDefinitionId = backendSessionQuery.data?.definition.id ?? 0
  const sessionDefinitionQuery = useSuspenseQuery(
    sessionDefinitionQueryOptions(sessionDefinitionId, contentfulLanguage),
  )

  const measurementDefinitions = useMemo(
    () => sessionDefinitionQuery.data?.measurementDefinitions ?? [],
    [sessionDefinitionQuery.data?.measurementDefinitions],
  )

  const measurementCount = measurementDefinitions.length
  const hasHydratedBackendResumeRef = useRef(false)
  const trackedResumeSessionIdRef = useRef<number | null>(null)
  const trackedCompletedSessionIdRef = useRef<number | null>(null)
  const shouldRejectSession = backendSessionQuery.data?.status === 'Aborted'

  const measurementLabels = useMemo<(string | null)[]>(() => {
    if (measurementDefinitions.length === 0) {
      return Array.from({ length: measurementCount }, () => null)
    }
    return measurementDefinitions.map((definition) => definition.name)
  }, [measurementDefinitions, measurementCount])

  const [state, dispatch] = useReducer(
    makeVoiceAssessmentReducer(0),
    0,
    (count) => buildInitialVoiceAssessmentState(count),
  )

  useEffect(() => {
    if (backendSessionQuery.error == null) {
      return
    }

    captureException(backendSessionQuery.error, {
      feature: 'voice-assessment',
      tags: { action: 'load_voice_assessment_session' },
    })
  }, [backendSessionQuery.error])

  useEffect(() => {
    if (sessionDefinitionQuery.error == null) {
      return
    }

    captureException(sessionDefinitionQuery.error, {
      feature: 'voice-assessment',
      tags: { action: 'load_voice_assessment_definition' },
    })
  }, [sessionDefinitionQuery.error])

  useEffect(() => {
    if (
      state.confirmedRecordingUris.length !== measurementCount ||
      state.confirmedRecordingBlobs.length !== measurementCount ||
      state.persistedMeasurementIds.length !== measurementCount ||
      state.measurementQuestionAnswers.length !== measurementCount
    ) {
      dispatch({ type: 'resize_measurements', count: measurementCount })
    }
  }, [
    measurementCount,
    state.confirmedRecordingBlobs.length,
    state.confirmedRecordingUris.length,
    state.measurementQuestionAnswers.length,
    state.persistedMeasurementIds.length,
  ])

  useEffect(() => {
    if (!shouldRejectSession) {
      return
    }

    replace('/(protected)/(account)/voice-assessment-history')
  }, [replace, shouldRejectSession])

  useEffect(() => {
    if (
      shouldRejectSession ||
      backendSessionQuery.data == null ||
      sessionDefinitionQuery.data == null ||
      hasHydratedBackendResumeRef.current
    ) {
      return
    }

    const backendResumeState = resolveBackendResumeTarget({
      measurementDefinitions:
        sessionDefinitionQuery.data.measurementDefinitions,
      measurements: backendSessionQuery.data.measurements,
      sessionStatus: backendSessionQuery.data.status,
    })

    if (backendResumeState.target.kind === 'finish') {
      replace(VOICE_ASSESSMENT_FINISH_ROUTE)
      hasHydratedBackendResumeRef.current = true
      return
    }

    const completedMeasurements = getVoiceAssessmentCompletedMeasurements({
      measurementIndex: backendResumeState.measurementIndex,
      measurementDefinitions,
    })
    if (
      completedMeasurements > 0 &&
      trackedResumeSessionIdRef.current !== backendSessionId
    ) {
      trackedResumeSessionIdRef.current = backendSessionId
      trackEvent(
        MixpanelEvents.VoiceAssessmentResumed,
        buildVoiceAssessmentResumedProperties({
          sessionId: backendSessionId,
          sessionDefinitionId: backendSessionQuery.data.definition.id,
          voiceType,
          totalMeasurements: measurementDefinitions.length,
          completedMeasurements,
          resumeMeasurementIndex: backendResumeState.measurementIndex,
        }),
      )
    }

    dispatch({
      type: 'hydrate',
      payload: {
        started: true,
        measurementIndex: backendResumeState.measurementIndex,
        persistedMeasurementIds: backendResumeState.persistedMeasurementIds,
      },
    })
    hasHydratedBackendResumeRef.current = true
  }, [
    backendSessionQuery.data,
    backendSessionId,
    measurementDefinitions,
    replace,
    sessionDefinitionQuery.data,
    shouldRejectSession,
    voiceType,
  ])

  const navigateAfterAssessment = useCallback(() => {
    if (isOnboardingEntry) {
      replace('/(protected)/(account)/(tabs)')
      return
    }

    if (canGoBack()) {
      back()
      return
    }

    replace('/(protected)/(account)/(tabs)')
  }, [back, canGoBack, isOnboardingEntry, replace])

  const handleSkip = useCallback(() => {
    if (abortSessionMutation.isPending) {
      return
    }

    if (sessionDefinitionId != null) {
      const reflection = getVoiceAssessmentReflectionAnalytics(
        state.personalReflection,
      )
      trackEvent(
        MixpanelEvents.VoiceAssessmentAbandoned,
        buildVoiceAssessmentAbandonedProperties({
          sessionId: backendSessionId,
          sessionDefinitionId,
          voiceType,
          measurementIndex: state.measurementIndex,
          completedMeasurements: getVoiceAssessmentCompletedMeasurements({
            measurementIndex: state.measurementIndex,
            measurementDefinitions,
          }),
          hasReflection: reflection.hasReflection,
          reflectionLength: reflection.reflectionLength,
        }),
      )
    }

    abortSessionMutation.mutate(backendSessionId, {
      onSuccess: () => {
        navigateAfterAssessment()
      },
      onError: (error) => {
        captureException(error, {
          feature: 'voice-assessment',
          tags: { action: 'abort_voice_assessment_session' },
        })
        navigateAfterAssessment()
      },
    })
  }, [
    abortSessionMutation,
    backendSessionId,
    measurementDefinitions,
    navigateAfterAssessment,
    sessionDefinitionId,
    state.measurementIndex,
    state.personalReflection,
    voiceType,
  ])

  const requestLeaveStandaloneAssessment = useCallback(() => {
    showAlert(
      t('onboarding:voice_assessment.leave_session_title'),
      t('onboarding:voice_assessment.leave_session_message'),
      [
        {
          text: t('onboarding:voice_assessment.leave_session_continue'),
          style: 'cancel',
        },
        {
          text: t('onboarding:voice_assessment.leave_session_leave'),
          style: 'destructive',
          onPress: handleSkip,
        },
      ],
    )
  }, [handleSkip, t])

  const [persistingMeasurementIndex, setPersistingMeasurementIndex] = useState<
    number | null
  >(null)

  const getMeasurementAnalytics = useCallback(
    (index: number) =>
      getVoiceAssessmentMeasurementAnalytics({
        measurementDefinitions,
        measurementQuestionAnswers: state.measurementQuestionAnswers,
        persistedMeasurementIds: state.persistedMeasurementIds,
        confirmedRecordingUris: state.confirmedRecordingUris,
        confirmedRecordingBlobs: state.confirmedRecordingBlobs,
        measurementIndex: index,
      }),
    [
      measurementDefinitions,
      state.confirmedRecordingBlobs,
      state.confirmedRecordingUris,
      state.measurementQuestionAnswers,
      state.persistedMeasurementIds,
    ],
  )

  const trackMeasurementRecordingCompleted = useCallback(
    (index: number) => {
      if (sessionDefinitionId == null) {
        return
      }
      const measurement = getMeasurementAnalytics(index)
      trackEvent(
        MixpanelEvents.VoiceAssessmentMeasurementRecordingCompleted,
        buildVoiceAssessmentMeasurementRecordingCompletedProperties({
          sessionId: backendSessionId,
          sessionDefinitionId,
          voiceType,
          measurementId: measurement.measurementId,
          measurementDefinitionId: measurement.measurementDefinitionId,
          measurementIndex: measurement.measurementIndex,
          hasQuestions: measurement.hasQuestions,
          hasRecording: measurement.hasRecording,
        }),
      )
    },
    [backendSessionId, getMeasurementAnalytics, sessionDefinitionId, voiceType],
  )

  const trackMeasurementQuestionsCompleted = useCallback(
    (index: number) => {
      if (sessionDefinitionId == null) {
        return
      }
      const measurement = getMeasurementAnalytics(index)
      trackEvent(
        MixpanelEvents.VoiceAssessmentMeasurementQuestionsCompleted,
        buildVoiceAssessmentMeasurementQuestionsCompletedProperties({
          sessionId: backendSessionId,
          sessionDefinitionId,
          voiceType,
          measurementId: measurement.measurementId,
          measurementDefinitionId: measurement.measurementDefinitionId,
          measurementIndex: measurement.measurementIndex,
          questionCount: measurement.questionCount,
          answeredQuestionCount: measurement.answeredQuestionCount,
        }),
      )
    },
    [backendSessionId, getMeasurementAnalytics, sessionDefinitionId, voiceType],
  )

  const persistMeasurement = useCallback(
    async (index: number): Promise<void> => {
      const measurementDefinition = measurementDefinitions[index]
      if (measurementDefinition == null) {
        return
      }
      const persistedMeasurementId =
        state.persistedMeasurementIds[index] ?? null
      const hasCreatedMeasurement =
        persistedMeasurementId != null &&
        (await hasSavedMeasurementData(queryClient, persistedMeasurementId))

      const clearPersistingMeasurementIndex = () => {
        setPersistingMeasurementIndex((current) =>
          current === index ? null : current,
        )
      }

      setPersistingMeasurementIndex(index)
      try {
        let measurementId = persistedMeasurementId
        if (!hasCreatedMeasurement) {
          measurementId = await submitVoiceAssessmentMeasurement({
            queryClient,
            sessionId: backendSessionId,
            measurementDefinition,
            confirmedRecordingUri: state.confirmedRecordingUris[index] ?? null,
            confirmedRecordingBlob:
              state.confirmedRecordingBlobs[index] ?? null,
            measurementQuestionAnswers:
              state.measurementQuestionAnswers[index] ?? [],
          })
          dispatch({
            type: 'set_measurement_persisted_id',
            index,
            value: measurementId,
          })
          if (sessionDefinitionId != null) {
            const measurement = getMeasurementAnalytics(index)
            trackEvent(
              MixpanelEvents.VoiceAssessmentMeasurementSubmitted,
              buildVoiceAssessmentMeasurementSubmittedProperties({
                sessionId: backendSessionId,
                sessionDefinitionId,
                voiceType,
                measurementId,
                measurementDefinitionId: measurement.measurementDefinitionId,
                measurementIndex: measurement.measurementIndex,
                hasRecording: measurement.hasRecording,
                questionCount: measurement.questionCount,
                answeredQuestionCount: measurement.answeredQuestionCount,
              }),
            )
          }
        }

        if (measurementId == null) {
          return
        }
      } catch (error) {
        const failedMessage = t(
          'onboarding:voice_assessment.upload_failed_message',
        )
        const missingMessage = t(
          'onboarding:voice_assessment.upload_missing_recordings_message',
        )
        const errorMessage = error instanceof Error ? error.message : ''
        const message =
          errorMessage === VOICE_ASSESSMENT_UPLOAD_ERROR.missingRecordings
            ? missingMessage
            : failedMessage

        if (
          errorMessage !== VOICE_ASSESSMENT_UPLOAD_ERROR.missingRecordings &&
          errorMessage !== VOICE_ASSESSMENT_UPLOAD_ERROR.authRequired
        ) {
          captureException(error, {
            feature: 'voice-assessment',
            tags: { action: 'submit_voice_assessment_measurement' },
          })
        }

        showToast({
          variant: 'error',
          title: t('onboarding:voice_assessment.upload_failed_title'),
          message,
          autoDismiss: false,
          action: {
            label: t('common:toast_retry'),
            onPress: () => {
              push(buildVoiceAssessmentRoute(backendSessionId))
            },
          },
        })
        throw error
      } finally {
        clearPersistingMeasurementIndex()
      }
    },
    [
      backendSessionId,
      getMeasurementAnalytics,
      measurementDefinitions,
      queryClient,
      sessionDefinitionId,
      state.confirmedRecordingBlobs,
      state.confirmedRecordingUris,
      state.measurementQuestionAnswers,
      state.persistedMeasurementIds,
      push,
      showToast,
      t,
      voiceType,
    ],
  )

  const handleCompleteAssessmentSuccess = useCallback(() => {
    if (
      sessionDefinitionId != null &&
      trackedCompletedSessionIdRef.current !== backendSessionId
    ) {
      trackedCompletedSessionIdRef.current = backendSessionId
      const reflection = getVoiceAssessmentReflectionAnalytics(
        state.personalReflection,
      )
      trackEvent(
        MixpanelEvents.VoiceAssessmentCompleted,
        buildVoiceAssessmentCompletedProperties({
          sessionId: backendSessionId,
          sessionDefinitionId,
          voiceType,
          hasReflection: reflection.hasReflection,
          reflectionLength: reflection.reflectionLength,
        }),
      )
    }
    showToast({
      variant: 'info',
      title: t('onboarding:voice_assessment.submit_success_title'),
      message: t('onboarding:voice_assessment.submit_success_message'),
    })
    replace(VOICE_ASSESSMENT_FINISH_ROUTE)
  }, [
    backendSessionId,
    replace,
    sessionDefinitionId,
    showToast,
    state.personalReflection,
    t,
    voiceType,
  ])

  const handleCompleteAssessmentError = useCallback(
    (error: unknown) => {
      const failedMessage = t(
        'onboarding:voice_assessment.upload_failed_message',
      )
      const missingMessage = t(
        'onboarding:voice_assessment.upload_missing_recordings_message',
      )

      const errorMessage = error instanceof Error ? error.message : ''
      const message =
        errorMessage === VOICE_ASSESSMENT_UPLOAD_ERROR.missingRecordings
          ? missingMessage
          : failedMessage

      if (
        errorMessage !== VOICE_ASSESSMENT_UPLOAD_ERROR.missingRecordings &&
        errorMessage !== VOICE_ASSESSMENT_UPLOAD_ERROR.authRequired
      ) {
        captureException(error, {
          feature: 'voice-assessment',
          tags: { action: 'submit_voice_assessment' },
        })
      }

      showToast({
        variant: 'error',
        title: t('onboarding:voice_assessment.upload_failed_title'),
        message,
        autoDismiss: false,
        action: {
          label: t('common:toast_retry'),
          onPress: () => {
            push(buildVoiceAssessmentRoute(backendSessionId))
          },
        },
      })
      navigateAfterAssessment()
    },
    [backendSessionId, navigateAfterAssessment, push, showToast, t],
  )

  const { mutate: mutateCompleteAssessment } = completeAssessmentMutation

  const isCompletingAssessment = completeAssessmentMutation.isPending

  const handleCompleteAssessment = useCallback(() => {
    mutateCompleteAssessment(
      {
        id: backendSessionId,
        comment: state.personalReflection,
      },
      {
        onSuccess: handleCompleteAssessmentSuccess,
        onError: handleCompleteAssessmentError,
      },
    )
  }, [
    backendSessionId,
    handleCompleteAssessmentError,
    handleCompleteAssessmentSuccess,
    mutateCompleteAssessment,
    state.personalReflection,
  ])

  const controller: VoiceAssessmentFlowController = {
    measurementCount,
    measurementDefinitions,
    measurementLabels,
    state,
    dispatch,
    requestLeaveStandaloneAssessment,
    isCompletingAssessment,
    handleCompleteAssessment,
    trackMeasurementRecordingCompleted,
    trackMeasurementQuestionsCompleted,
    persistMeasurement,
    persistingMeasurementIndex,
  }

  return {
    backendSessionQuery,
    controller,
    sessionDefinitionQuery,
    shouldRejectSession,
  }
}
